import { useCallback, useEffect, useState } from "react";
import { Bell, Camera, Clock3, Download, FileText, Lightbulb, Loader2, Lock, Pencil, Plus, ReceiptText, Scale, Settings, Trash2, Upload, Users } from "lucide-react";
import { platformClient, formatPlatformError } from "./platformApi";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CARD = "border border-white/10 bg-coal p-6";
const LABEL = "text-[10px] uppercase tracking-[0.25em] text-ash";
const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";

const EMPTY_EXP = { date: new Date().toISOString().slice(0, 10), supplier: "", description: "", category: "overig", amount_incl: "", vat_rate: 21, vat_shifted: false, supplier_country: "NL" };

// ── Klantflow-gids — stap voor stap wat je doet per klant ──
const FLOW_STEPS = [
    {
        stap: 1,
        titel: "Klant aanmaken",
        waar: "Boekhouding → Mijn klanten",
        wat: "Voeg de klant toe met bedrijfsnaam, contactpersoon, adres, KvK en BTW-nummer. Dit is de basis — alles vult hierna automatisch in.",
        klaar: "Klant staat in je klantenbestand",
    },
    {
        stap: 2,
        titel: "Offerte maken",
        waar: "Platform → Voorstellen",
        wat: "Kies de klant, vul de werkzaamheden in (eenmalig, uren of maandelijks), en voeg een inleidende tekst toe. Download als PDF of bewerk in Word.",
        klaar: "Offerte is verstuurd naar de klant",
    },
    {
        stap: 3,
        titel: "Offerte akkoord",
        waar: "Platform → Voorstellen",
        wat: "Klant gaat akkoord? Markeer de offerte als 'Akkoord'. Wil je eerst een contract of opdrachtbevestiging? Maak die dan nu aan via de tabs boven het formulier.",
        klaar: "Offerte is akkoord (of contract is ondertekend)",
    },
    {
        stap: 4,
        titel: "Contract of opdrachtbevestiging (optioneel)",
        waar: "Platform → Voorstellen → tab 'Contract' of 'Opdrachtbevestiging'",
        wat: "Bij grotere opdrachten: maak een contract of opdrachtbevestiging aan met dezelfde klantgegevens. Download als Word, pas aan, upload terug als definitieve versie.",
        klaar: "Contract is ondertekend door beide partijen",
    },
    {
        stap: 5,
        titel: "Werk uitvoeren + uren bijhouden",
        waar: "Boekhouding → Uren",
        wat: "Voer de opdracht uit. Houd je uren bij in de urenregistratie — dat heb je nodig voor de Belastingdienst (urencriterium).",
        klaar: "Werk is afgerond, uren zijn geregistreerd",
    },
    {
        stap: 6,
        titel: "Factuur maken",
        waar: "Platform → Facturen",
        wat: "Maak een factuur vanuit de akkoord-offerte (klik 'Factuur' bij de offerte) of maak een losse factuur. Je IBAN en bedrijfsgegevens staan er automatisch op.",
        klaar: "Factuur is verstuurd naar de klant",
    },
    {
        stap: 7,
        titel: "BTW-aangifte",
        waar: "Boekhouding → BTW-overzicht",
        wat: "Aan het einde van het kwartaal: download het Belastingdienst-rapport. Daar staat alles in wat je nodig hebt voor je aangifte.",
        klaar: "BTW-aangifte is ingediend",
    },
];

export const BooksFlowGuideSection = () => {
    const [open, setOpen] = useState(false);
    return (
        <div className={`${CARD} mt-6`} data-testid="flow-guide-section">
            <button
                type="button"
                data-testid="flow-guide-toggle"
                onClick={() => setOpen(!open)}
                className="flex w-full items-center justify-between text-left"
            >
                <p className={LABEL}>
                    <Lightbulb className="mr-2 inline h-3.5 w-3.5 text-gold" />
                    Werkwijze per klant — stap voor stap
                </p>
                <span className="text-xs text-ash">{open ? "Sluiten" : "Openen"}</span>
            </button>
            {open && (
                <div className="mt-5 space-y-4">
                    {FLOW_STEPS.map((s) => (
                        <div key={s.stap} data-testid={`flow-step-${s.stap}`} className="flex gap-4 border-b border-white/5 pb-4">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-gold/30 text-sm font-bold text-gold">
                                {s.stap}
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-bone">{s.titel}</p>
                                <p className="mt-0.5 text-[10px] uppercase tracking-[0.15em] text-gold/70">{s.waar}</p>
                                <p className="mt-1 text-xs leading-relaxed text-ash">{s.wat}</p>
                                <p className="mt-1 text-[10px] text-green-400/70">Klaar als: {s.klaar}</p>
                            </div>
                        </div>
                    ))}
                    <div className="mt-4 border border-gold/20 bg-gold/5 p-4">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gold">Tip</p>
                        <p className="mt-1 text-xs leading-relaxed text-ash">
                            Bij kleine opdrachten (een paar uur consultancy) heb je alleen stap 1, 2, 6 en 7 nodig.
                            Contracten en opdrachtbevestigingen zijn vooral handig bij grotere projecten of langlopende samenwerkingen.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};


export const BooksExpensesSection = () => {
    const [expenses, setExpenses] = useState([]);
    const [advice, setAdvice] = useState(null);
    const [form, setForm] = useState(EMPTY_EXP);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [receiptFile, setReceiptFile] = useState(null);
    const [receiptReading, setReceiptReading] = useState(false);

    const load = useCallback(async () => {
        try {
            const [ex, adv] = await Promise.all([
                platformClient.get("/admin/books/expenses"),
                platformClient.get("/admin/books/advice"),
            ]);
            setExpenses(ex.data);
            setAdvice(adv.data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const flash = (msg) => {
        setNotice(msg);
        setTimeout(() => setNotice(""), 5000);
    };

    const readReceipt = async (file) => {
        if (!file) return;
        setError("");
        setReceiptFile(file);
        setReceiptReading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await platformClient.post("/admin/books/receipts/read", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((f) => ({
                ...f,
                date: data.date || f.date,
                supplier: data.supplier || f.supplier,
                description: data.description || f.description,
                amount_incl: data.amount_incl ? String(data.amount_incl) : f.amount_incl,
                vat_rate: data.vat_rate ?? f.vat_rate,
            }));
            flash("Bonnetje gelezen — controleer de velden en sla op");
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail) || "Bonnetje kon niet worden gelezen");
        } finally {
            setReceiptReading(false);
        }
    };

    const addExpense = async (e) => {
        e.preventDefault();
        setError("");
        try {
            const { data: created } = await platformClient.post("/admin/books/expenses", { ...form, amount_incl: Number(form.amount_incl) || 0, vat_rate: Number(form.vat_rate) });
            if (receiptFile) {
                const fd = new FormData();
                fd.append("file", receiptFile);
                await platformClient.post(`/admin/books/expenses/${created.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            }
            setForm(EMPTY_EXP);
            setReceiptFile(null);
            flash("Uitgave opgeslagen");
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const uploadReceipt = async (id, file) => {
        if (!file) return;
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            await platformClient.post(`/admin/books/expenses/${id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            flash("Bonnetje opgeslagen");
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const openReceipt = async (id) => {
        const { data } = await platformClient.get(`/admin/books/expenses/${id}/receipt`, { responseType: "blob" });
        window.open(URL.createObjectURL(new Blob([data])), "_blank");
    };

    const remove = async (id) => {
        await platformClient.delete(`/admin/books/expenses/${id}`);
        load();
    };

    const catAdvice = (key) => (advice?.categories || []).find((c) => c.key === key);

    return (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className={CARD}>
                <p className={LABEL}><Plus className="mr-2 inline h-3.5 w-3.5 text-gold" />Nieuwe uitgave</p>
                <label data-testid="receipt-read-label" className={`mt-4 flex cursor-pointer items-center justify-center gap-2 border border-dashed px-4 py-3 text-xs uppercase tracking-[0.15em] transition-colors ${receiptFile ? "border-gold/60 bg-gold/5 text-gold" : "border-white/20 text-ash hover:border-gold/50 hover:text-gold"}`}>
                    {receiptReading ? (
                        <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Bonnetje wordt gelezen…</>
                    ) : receiptFile ? (
                        <><Upload className="h-3.5 w-3.5" /> {receiptFile.name} — klik om te vervangen</>
                    ) : (
                        <><Camera className="h-3.5 w-3.5" /> Foto van bonnetje — velden vullen zichzelf in</>
                    )}
                    <input data-testid="receipt-read-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => readReceipt(e.target.files[0])} />
                </label>
                <form onSubmit={addExpense} className="mt-4 grid grid-cols-2 gap-5">
                    <label className="block">
                        <span className={LABEL}>Datum</span>
                        <input data-testid="expense-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className={inputCls} />
                    </label>
                    <label className="block">
                        <span className={LABEL}>Leverancier</span>
                        <input data-testid="expense-supplier" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} required placeholder="bijv. Bol.com" className={inputCls} />
                    </label>
                    <label className="col-span-2 block">
                        <span className={LABEL}>Omschrijving</span>
                        <input data-testid="expense-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Wat heb je gekocht?" className={inputCls} />
                    </label>
                    <label className="block">
                        <span className={LABEL}>Categorie</span>
                        <select data-testid="expense-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`${inputCls} bg-coal`}>
                            {(advice?.categories || []).map((c) => (
                                <option key={c.key} value={c.key}>{c.label}</option>
                            ))}
                        </select>
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                        <label className="block">
                            <span className={LABEL}>Bedrag incl. btw</span>
                            <input data-testid="expense-amount" type="number" step="0.01" min="0.01" value={form.amount_incl} onChange={(e) => setForm({ ...form, amount_incl: e.target.value })} required className={inputCls} />
                        </label>
                        <label className="block">
                            <span className={LABEL}>BTW</span>
                            <select data-testid="expense-vat" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value, vat_shifted: false })} className={`${inputCls} bg-coal`}>
                                <option value={21}>21%</option>
                                <option value={9}>9%</option>
                                <option value={0}>0% (horeca/representatie)</option>
                            </select>
                        </label>
                    </div>
                    <label className="col-span-2 flex items-start gap-3 border border-white/10 p-3">
                        <input data-testid="expense-vat-shifted" type="checkbox" checked={form.vat_shifted || false} onChange={(e) => setForm({ ...form, vat_shifted: e.target.checked, vat_rate: e.target.checked ? 0 : 21 })} className="mt-0.5 h-4 w-4 accent-gold" />
                        <span className="text-xs leading-relaxed text-ash">
                            <span className="block text-bone">BTW verlegd (buitenlandse leverancier, bv. Emergent)</span>
                            Geen btw op de factuur — jij geeft dit op in rubriek 4a bij je btw-aangifte
                        </span>
                    </label>
                    {form.vat_shifted && (
                        <label className="col-span-2 block">
                            <span className={LABEL}>Land leverancier</span>
                            <select data-testid="expense-country" value={form.supplier_country || "VS"} onChange={(e) => setForm({ ...form, supplier_country: e.target.value })} className={`${inputCls} bg-coal`}>
                                <option value="VS">Verenigde Staten</option>
                                <option value="BE">België</option>
                                <option value="DE">Duitsland</option>
                                <option value="overig">Overig</option>
                            </select>
                        </label>
                    )}
                    {catAdvice(form.category) ? (
                        <p data-testid="expense-category-advice" className="col-span-2 border border-gold/20 bg-gold/5 p-3 text-xs leading-relaxed text-ash">
                            <Lightbulb className="mr-2 inline h-3.5 w-3.5 text-gold" />
                            {catAdvice(form.category).advice} <span className="text-gold">Aftrekbaar: {catAdvice(form.category).deductible}.</span>
                        </p>
                    ) : null}
                    <div className="col-span-2">
                        {error ? <p data-testid="expense-error" className="mb-2 text-sm text-red-400">{error}</p> : null}
                        {notice ? <p data-testid="expense-notice" className="mb-2 text-xs uppercase tracking-[0.15em] text-gold">{notice}</p> : null}
                        <button data-testid="expense-submit" type="submit" className="border border-gold/50 px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black">
                            Uitgave opslaan
                        </button>
                    </div>
                </form>
            </div>

            <div className={CARD}>
                <p className={LABEL}><ReceiptText className="mr-2 inline h-3.5 w-3.5 text-gold" />Uitgaven & bonnetjes</p>
                {expenses.length === 0 ? (
                    <p data-testid="expenses-empty" className="mt-4 text-sm text-ash">Nog geen uitgaven. Voeg links je eerste bonnetje toe.</p>
                ) : (
                    <div className="mt-4 max-h-[420px] space-y-2 overflow-y-auto">
                        {expenses.map((x) => (
                            <div key={x.id} data-testid={`expense-row-${x.id}`} className="flex items-center justify-between gap-3 border-b border-white/5 pb-2 text-xs">
                                <div className="min-w-0">
                                    <p className="truncate text-bone">{x.supplier} <span className="text-ash">— {x.description || "—"}</span></p>
                                    <p className="text-[10px] uppercase tracking-wide text-ash">{x.date} · btw {euro(x.vat_amount)}</p>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    <span className="text-bone">{euro(x.amount_incl)}</span>
                                    {x.receipt_path ? (
                                        <button data-testid={`expense-receipt-${x.id}`} onClick={() => openReceipt(x.id)} className="border border-gold/40 px-2 py-1 text-[10px] uppercase text-gold hover:bg-gold/10">Bon</button>
                                    ) : (
                                        <label data-testid={`expense-upload-${x.id}`} className="flex cursor-pointer items-center gap-1 border border-white/15 px-2 py-1 text-[10px] uppercase text-ash hover:border-gold/50 hover:text-gold">
                                            <Camera className="h-3 w-3" /> Bon
                                            <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => uploadReceipt(x.id, e.target.files[0])} />
                                        </label>
                                    )}
                                    <button data-testid={`expense-delete-${x.id}`} onClick={() => remove(x.id)} className="text-ash transition-colors hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const VAT_DEADLINES = { 1: "30 april", 2: "31 juli", 3: "31 oktober", 4: "31 januari" };
const VAT_LABELS = { 1: "Q1 (jan–mrt)", 2: "Q2 (apr–jun)", 3: "Q3 (jul–sep)", 4: "Q4 (okt–dec)" };

export const BooksBackupSection = () => {
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState("");

    const download = async () => {
        setBusy(true);
        setDone("");
        try {
            const { data } = await platformClient.get("/admin/backup", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([data], { type: "application/json" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `clarke-backup-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            setDone("Backup gedownload — bewaar dit bestand op een veilige plek (bijv. je eigen computer + een USB-stick of cloudmap).");
        } catch {
            setDone("Backup downloaden mislukt — probeer het opnieuw.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className={`${CARD} mt-6`} data-testid="backup-section">
            <p className={LABEL}><Lock className="mr-2 inline h-3.5 w-3.5 text-gold" />Backup & beveiliging</p>
            <p className="mt-2 text-xs leading-relaxed text-ash">
                Jouw data is van jou. Download regelmatig een volledige reservekopie van alles — klanten, offertes,
                contracten, facturen, boekhouding, uren, uitgaven, documenten en handtekeningen.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
                <button data-testid="backup-download" onClick={download} disabled={busy} className="flex items-center gap-2 border border-gold/50 px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black disabled:opacity-40">
                    <Download className="h-3.5 w-3.5" /> {busy ? "Bezig…" : "Volledige backup downloaden"}
                </button>
            </div>
            {done && <p data-testid="backup-done" className="mt-3 text-xs text-ash">{done}</p>}
            <div className="mt-5 border-t border-white/10 pt-4">
                <p className={LABEL}>Zo is je platform beveiligd</p>
                <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-ash">
                    <li>— Wachtwoorden worden nooit leesbaar opgeslagen (onkraysbare bcrypt-hashing)</li>
                    <li>— Inloggen wordt geblokkeerd na te veel mislukte pogingen (account-lockout + IP-limiet)</li>
                    <li>— Publieke tekenpagina's gebruiken ongusbare links en zijn begrensd tegen misbruik</li>
                    <li>— Alle verkeer loopt versleuteld via HTTPS; alleen jij (admin) kan bij je administratie</li>
                    <li>— Uploads zijn beperkt tot 10MB en alleen Word-documenten worden geaccepteerd</li>
                    <li>— Het platform verstuurt zelf geen e-mails — jij verstuurt alles zelf vanuit je eigen mail</li>
                </ul>
            </div>
        </div>
    );
};

export const BooksTemplatesSection = () => {
    const [templates, setTemplates] = useState(null);
    const [saved, setSaved] = useState(false);
    const [open, setOpen] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/admin/contract-templates");
            setTemplates(data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = async () => {
        setSaved(false);
        try {
            await platformClient.put("/admin/contract-templates", templates);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch {}
    };

    if (!templates) return null;
    const types = [["consultancy", "Consultancy"], ["bemiddeling", "Bemiddeling"], ["software", "Software / website"]];
    return (
        <div className={`${CARD} mt-6`} data-testid="templates-section">
            <p className={LABEL}><FileText className="mr-2 inline h-3.5 w-3.5 text-gold" />Mijn standaard contractteksten</p>
            <p className="mt-2 text-xs text-ash">
                Deze teksten worden automatisch ingevuld bij nieuwe offertes, contracten en opdrachtbevestigingen per opdrachttype.
                Pas ze hier eenmalig aan — daarna hoef je ze nooit meer opnieuw te schrijven. Gebruik **tekst** voor dikgedrukt.
            </p>
            <div className="mt-4 space-y-3">
                {types.map(([key, label]) => (
                    <div key={key} className="border border-white/10">
                        <button type="button" data-testid={`template-toggle-${key}`} onClick={() => setOpen(open === key ? null : key)} className="flex w-full items-center justify-between px-4 py-3 text-left">
                            <span className="text-sm text-bone">{label}</span>
                            <span className="text-[10px] uppercase tracking-wide text-ash">{open === key ? "Sluiten" : "Bewerken"}</span>
                        </button>
                        {open === key && (
                            <textarea
                                data-testid={`template-edit-${key}`}
                                rows={10}
                                value={templates[key] || ""}
                                onChange={(e) => setTemplates({ ...templates, [key]: e.target.value })}
                                className={`${inputCls} mx-4 mb-4 w-[calc(100%-2rem)] font-mono text-xs leading-relaxed`}
                            />
                        )}
                    </div>
                ))}
            </div>
            <div className="mt-4 flex items-center gap-3">
                <button data-testid="templates-save" onClick={save} className="border border-gold/50 px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black">
                    Teksten opslaan
                </button>
                {saved && <span data-testid="templates-saved" className="text-xs uppercase tracking-[0.15em] text-gold">Opgeslagen</span>}
            </div>
        </div>
    );
};


export const BooksLayoutSection = () => {
    const [layout, setLayout] = useState(null);
    const [saved, setSaved] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/admin/offerte-layout");
            setLayout(data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = async (next) => {
        setLayout(next);
        setSaved(false);
        try {
            await platformClient.put("/admin/offerte-layout", next);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch {}
    };

    if (!layout) return null;
    return (
        <div className={`${CARD} mt-6`} data-testid="layout-section">
            <p className={LABEL}><Settings className="mr-2 inline h-3.5 w-3.5 text-gold" />Opmaak offertes — teksten en kleuren</p>
            <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                <label className="block">
                    <span className={LABEL}>Titel bovenaan (bijv. OFFERTE)</span>
                    <input data-testid="layout-doc-label" value={layout.doc_label || ""} onChange={(e) => setLayout({ ...layout, doc_label: e.target.value })} className={inputCls} />
                </label>
                <label className="block">
                    <span className={LABEL}>Titelgrootte</span>
                    <input data-testid="layout-title-size" type="number" min="10" max="24" value={layout.title_size || 15} onChange={(e) => setLayout({ ...layout, title_size: Number(e.target.value) })} className={inputCls} />
                </label>
                <label className="col-span-2 block">
                    <span className={LABEL}>Standaard intro-tekst (bijv. Beste {"{klant}"}, hierbij mijn offerte...)</span>
                    <textarea data-testid="layout-intro" rows={3} value={layout.default_intro || ""} onChange={(e) => setLayout({ ...layout, default_intro: e.target.value })} className={inputCls} />
                </label>
                <label className="block">
                    <span className={LABEL}>Accentkleur (goud)</span>
                    <input data-testid="layout-accent" type="color" value={layout.accent_kleur || "#c9a227"} onChange={(e) => setLayout({ ...layout, accent_kleur: e.target.value })} className="h-10 w-20 cursor-pointer border border-white/20" />
                </label>
                <label className="block">
                    <span className={LABEL}>Logo-breedte (mm): {layout.logo_width_mm || 38}</span>
                    <input data-testid="layout-logo-width" type="range" min="20" max="80" value={layout.logo_width_mm || 38} onChange={(e) => setLayout({ ...layout, logo_width_mm: Number(e.target.value) })} className="mt-3 w-full accent-gold" />
                </label>
                <label className="flex items-center gap-3">
                    <input data-testid="layout-av-ref" type="checkbox" checked={layout.algemene_voorwaarden_ref !== false} onChange={(e) => setLayout({ ...layout, algemene_voorwaarden_ref: e.target.checked })} className="h-4 w-4 accent-gold" />
                    <span className="text-sm text-bone">Verwijzing naar algemene voorwaarden op offerte</span>
                </label>
            </div>
            <div className="mt-5 flex items-center gap-3">
                <button data-testid="layout-save" onClick={() => save(layout)} className="border border-gold/50 px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black">
                    Opslaan
                </button>
                {saved && <span data-testid="layout-saved" className="text-xs uppercase tracking-[0.15em] text-gold">Opgeslagen</span>}
            </div>
        </div>
    );
};


const EMPTY_CLIENT = { company: "", client_type: "consultancy", contact_name: "", contact_function: "", email: "", phone: "", address: "", kvk: "", btw_number: "", notes: "" };

export const BooksClientsSection = () => {
    const [clients, setClients] = useState([]);
    const [form, setForm] = useState(EMPTY_CLIENT);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/admin/clients");
            setClients(data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const flash = (m) => {
        setNotice(m);
        setTimeout(() => setNotice(""), 4000);
    };

    const save = async (e) => {
        e.preventDefault();
        setError("");
        try {
            if (editing) {
                await platformClient.patch(`/admin/clients/${editing}`, form);
                flash("Klant bijgewerkt");
            } else {
                await platformClient.post("/admin/clients", form);
                flash("Klant aangemaakt — nu te kiezen bij offertes");
            }
            setForm(EMPTY_CLIENT);
            setEditing(null);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const startEdit = (c) => {
        setEditing(c.id);
        setForm({ company: c.company, client_type: c.client_type || "consultancy", contact_name: c.contact_name || "", contact_function: c.contact_function || "", email: c.email || "", phone: c.phone || "", address: c.address || "", kvk: c.kvk || "", btw_number: c.btw_number || "", notes: c.notes || "" });
    };

    const remove = async (id) => {
        await platformClient.delete(`/admin/clients/${id}`);
        load();
    };

    return (
        <div className={`${CARD} mt-6`} data-testid="clients-section">
            <p className={LABEL}><Users className="mr-2 inline h-3.5 w-3.5 text-gold" />Mijn klanten — gegevens voor offertes & facturen</p>
            <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2">
                <form onSubmit={save} className="grid grid-cols-2 gap-4">
                    <label className="col-span-2 block">
                        <span className={LABEL}>Bedrijfsnaam</span>
                        <input data-testid="client-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} required className={inputCls} />
                    </label>
                    <label className="block">
                        <span className={LABEL}>Type klant</span>
                        <select data-testid="client-type" value={form.client_type} onChange={(e) => setForm({ ...form, client_type: e.target.value })} className={`${inputCls} bg-coal`}>
                            <option value="consultancy">Consultancy</option>
                            <option value="bemiddeling">Bemiddeling</option>
                            <option value="software">Software (Clarke Platform)</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className={LABEL}>Contactpersoon</span>
                        <input data-testid="client-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inputCls} />
                    </label>
                    <label className="block">
                        <span className={LABEL}>Functie (voor handtekening)</span>
                        <input data-testid="client-function" value={form.contact_function} onChange={(e) => setForm({ ...form, contact_function: e.target.value })} placeholder="bijv. Directeur" className={inputCls} />
                    </label>
                    <label className="block">
                        <span className={LABEL}>E-mail</span>
                        <input data-testid="client-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                    </label>
                    <label className="block">
                        <span className={LABEL}>Telefoon</span>
                        <input data-testid="client-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                    </label>
                    <label className="col-span-2 block">
                        <span className={LABEL}>Adres (regels met enter)</span>
                        <textarea data-testid="client-address" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} />
                    </label>
                    <label className="block">
                        <span className={LABEL}>KvK-nummer</span>
                        <input data-testid="client-kvk" value={form.kvk} onChange={(e) => setForm({ ...form, kvk: e.target.value })} className={inputCls} />
                    </label>
                    <label className="block">
                        <span className={LABEL}>BTW-nummer</span>
                        <input data-testid="client-btw" value={form.btw_number} onChange={(e) => setForm({ ...form, btw_number: e.target.value })} placeholder="NL123456789B01" className={inputCls} />
                    </label>
                    <div className="flex items-end">
                        {error ? <p data-testid="client-error" className="text-sm text-red-400">{error}</p> : null}
                        {notice ? <p data-testid="client-notice" className="text-xs uppercase tracking-[0.15em] text-gold">{notice}</p> : null}
                    </div>
                    <div className="col-span-2 flex items-center gap-3">
                        <button data-testid="client-submit" type="submit" className="border border-gold/50 px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black">
                            {editing ? "Klant bijwerken" : "+ Klant toevoegen"}
                        </button>
                        {editing && <button type="button" onClick={() => { setEditing(null); setForm(EMPTY_CLIENT); }} className="text-xs uppercase tracking-[0.15em] text-ash hover:text-bone">Annuleren</button>}
                    </div>
                </form>
                <div>
                    {clients.length === 0 ? (
                        <p data-testid="clients-empty" className="mt-1 text-sm text-ash">Nog geen klanten. Voeg je eerste klant toe — zijn gegevens vullen dan automatisch op offertes in.</p>
                    ) : (
                        <div className="max-h-[380px] space-y-2 overflow-y-auto">
                            {clients.map((c) => (
                                <div key={c.id} data-testid={`client-row-${c.id}`} className="flex items-center justify-between gap-3 border-b border-white/5 pb-2 text-xs">
                                    <div className="min-w-0">
                                        <p className="truncate text-bone">{c.company} <span className="text-ash">— {c.contact_name || "geen contact"}</span></p>
                                        <p className="text-[10px] uppercase tracking-wide text-ash">{c.email || "—"} · {c.kvk || "geen kvk"}</p>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-2">
                                        <button data-testid={`client-edit-${c.id}`} onClick={() => startEdit(c)} className="border border-white/15 px-2 py-1 text-[10px] uppercase text-ash hover:border-gold/50 hover:text-gold">Bewerk</button>
                                        <button data-testid={`client-delete-${c.id}`} onClick={() => remove(c.id)} className="text-ash transition-colors hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


export const BooksDocumentsSection = () => {
    const [docs, setDocs] = useState([]);
    const [open, setOpen] = useState(null);
    const [body, setBody] = useState("");
    const [title, setTitle] = useState("");
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/admin/documents");
            setDocs(data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const flash = (m) => {
        setNotice(m);
        setTimeout(() => setNotice(""), 4000);
    };

    const openDoc = async (key) => {
        setError("");
        try {
            const { data } = await platformClient.get(`/admin/documents/${key}`);
            setOpen(data);
            setTitle(data.title);
            setBody(data.body);
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const saveDoc = async () => {
        if (!open) return;
        try {
            await platformClient.put(`/admin/documents/${open.key}`, { title, body });
            flash(`${title} opgeslagen`);
            setOpen(null);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const downloadDocx = async (key, name) => {
        const { data } = await platformClient.get(`/admin/documents/${key}/docx`, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `${name}.docx`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const uploadDocx = async (key, file) => {
        if (!file) return;
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            await platformClient.post(`/admin/documents/${key}/docx-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            flash("Word-versie opgeslagen als definitieve PDF");
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const revertDoc = async (key) => {
        await platformClient.delete(`/admin/documents/${key}/custom-pdf`);
        flash("Terug naar platform-versie");
        load();
    };

    if (open) {
        return (
            <div className={`${CARD} mt-6`} data-testid="document-editor">
                <div className="flex items-center justify-between">
                    <p className={LABEL}><FileText className="mr-2 inline h-3.5 w-3.5 text-gold" />Document bewerken</p>
                    <button data-testid="document-close" onClick={() => setOpen(null)} className="text-ash hover:text-bone">✕</button>
                </div>
                <label className="mt-4 block">
                    <span className={LABEL}>Titel</span>
                    <input data-testid="document-title" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
                </label>
                <label className="mt-4 block">
                    <span className={LABEL}>Inhoud — gebruik **tekst** voor dikgedrukt, lege regel = nieuwe alinea</span>
                    <textarea data-testid="document-body" rows={18} value={body} onChange={(e) => setBody(e.target.value)} className={`${inputCls} font-mono text-xs leading-relaxed`} />
                </label>
                {open.jurist_check && (
                    <p className="mt-3 border border-gold/30 bg-gold/5 p-3 text-xs leading-relaxed text-ash">
                        <Scale className="mr-2 inline h-3.5 w-3.5 text-gold" />
                        Dit is een concept — laat het beoordelen door een jurist voordat je het naar klanten stuurt.
                    </p>
                )}
                {error ? <p data-testid="document-error" className="mt-3 text-sm text-red-400">{error}</p> : null}
                <div className="mt-5 flex items-center gap-3">
                    <button data-testid="document-save" onClick={saveDoc} className="border border-gold/50 px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black">
                        Opslaan
                    </button>
                    <button onClick={() => setOpen(null)} className="text-xs uppercase tracking-[0.15em] text-ash hover:text-bone">Annuleren</button>
                </div>
            </div>
        );
    }

    return (
        <div className={`${CARD} mt-6`} data-testid="documents-section">
            <p className={LABEL}><FileText className="mr-2 inline h-3.5 w-3.5 text-gold" />Mijn documenten — algemene voorwaarden, privacy &amp; verwerking</p>
            <p className="mt-2 text-xs text-ash">Bewerk ze hier zelf, download als PDF of Word, of zet een in Word bewerkte versie terug als definitieve PDF.</p>
            <div className="mt-4 space-y-2">
                {docs.map((d) => (
                    <div key={d.key} data-testid={`document-row-${d.key}`} className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 pb-2 text-xs">
                        <div className="min-w-0">
                            <p className="text-bone">{d.title} {d.jurist_check ? <span className="text-[9px] uppercase tracking-wide text-gold">· concept — jurist checken</span> : null}</p>
                            <p className="text-[10px] text-ash">{d.subtitle}{d.has_custom_pdf ? " · eigen Word-versie actief" : ""}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            <button data-testid={`document-edit-${d.key}`} onClick={() => openDoc(d.key)} className="flex items-center gap-1 border border-white/15 px-2 py-1 text-[10px] uppercase text-ash hover:border-gold/50 hover:text-gold">
                                <Pencil className="h-3 w-3" /> Bewerk
                            </button>
                            <a data-testid={`document-pdf-${d.key}`} href={`${platformClient.defaults.baseURL}/admin/documents/${d.key}/pdf`} target="_blank" rel="noreferrer" className="flex items-center gap-1 border border-gold/40 px-2 py-1 text-[10px] uppercase text-gold hover:bg-gold/10">
                                <Download className="h-3 w-3" /> PDF
                            </a>
                            <button data-testid={`document-word-${d.key}`} onClick={() => downloadDocx(d.key, d.key)} className="flex items-center gap-1 border border-white/15 px-2 py-1 text-[10px] uppercase text-ash hover:border-gold/50 hover:text-gold">
                                <FileText className="h-3 w-3" /> Word
                            </button>
                            <label data-testid={`document-upload-${d.key}`} className="flex cursor-pointer items-center gap-1 border border-white/15 px-2 py-1 text-[10px] uppercase text-ash hover:border-gold/50 hover:text-gold">
                                <Upload className="h-3 w-3" />
                                <input type="file" accept=".docx" className="hidden" onChange={(e) => uploadDocx(d.key, e.target.files[0])} />
                            </label>
                            {d.has_custom_pdf ? (
                                <button data-testid={`document-revert-${d.key}`} onClick={() => revertDoc(d.key)} className="border border-gold/40 px-2 py-1 text-[9px] uppercase text-gold hover:bg-gold/10">Eigen versie ✕</button>
                            ) : null}
                        </div>
                    </div>
                ))}
            </div>
            {error ? <p data-testid="documents-error" className="mt-3 text-sm text-red-400">{error}</p> : null}
            {notice ? <p data-testid="documents-notice" className="mt-3 text-xs uppercase tracking-[0.15em] text-gold">{notice}</p> : null}
        </div>
    );
};

export const BooksNotificationsSection = () => {
    const [prefs, setPrefs] = useState(null);
    const [saved, setSaved] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/admin/notifications");
            setPrefs(data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = async (next) => {
        setPrefs(next);
        setSaved(false);
        try {
            await platformClient.put("/admin/notifications", next);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch {}
    };

    if (!prefs) return null;
    const Toggle = ({ k, label, hint }) => (
        <button
            type="button"
            data-testid={`notif-toggle-${k}`}
            onClick={() => save({ ...prefs, [k]: !prefs[k] })}
            className="flex w-full items-center justify-between border border-white/10 bg-black/40 px-4 py-3 text-left transition-colors hover:border-gold/40"
        >
            <span>
                <span className="block text-sm text-bone">{label}</span>
                <span className="block text-[11px] text-ash">{hint}</span>
            </span>
            <span className={`ml-4 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${prefs[k] ? "bg-gold" : "bg-white/15"}`}>
                <span className={`h-5 w-5 rounded-full bg-white transition-transform ${prefs[k] ? "translate-x-5" : "translate-x-0.5"}`} />
            </span>
        </button>
    );

    return (
        <div className={`${CARD} mt-6`} data-testid="notifications-section">
            <p className={LABEL}><Bell className="mr-2 inline h-3.5 w-3.5 text-gold" />Mijn meldingen — wat mag het platform je mailen?</p>
            <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                <Toggle k="btw_reminder" label="BTW-aangifte herinnering" hint="Aan het begin van elke aangiftemaand (jan/apr/jul/okt), alleen als er boekingen zijn" />
                <Toggle k="uren_reminder" label="Uren-seintje (wekelijks)" hint="Elke maandag als je je uren nog niet hebt gelogd voor het urencriterium" />
                <Toggle k="contact_emails" label="Contactberichten" hint="Mail bij elk nieuw bericht via het contactformulier op je site" />
                <Toggle k="offerte_emails" label="Offerte-meldingen" hint="Mail wanneer een klant een offerte digitaal akkoord bevindt" />
            </div>
            {prefs.btw_reminder && (
                <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-white/10 pt-4">
                    <span className={LABEL}>BTW-herinnering versturen</span>
                    <select
                        data-testid="notif-btw-frequency"
                        value={prefs.btw_frequency || "1x"}
                        onChange={(e) => save({ ...prefs, btw_frequency: e.target.value })}
                        className="border border-white/15 bg-coal px-3 py-1.5 text-xs text-bone"
                    >
                        <option value="1x">1× per aangiftemaand</option>
                        <option value="2x">2× per aangiftemaand</option>
                        <option value="elke_week">Elke week in de aangiftemaand</option>
                    </select>
                    {saved && <span data-testid="notif-saved" className="text-xs uppercase tracking-[0.15em] text-gold">Opgeslagen</span>}
                </div>
            )}
        </div>
    );
};


export const BooksVatSection = () => {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [quarter, setQuarter] = useState(Math.ceil((now.getMonth() + 1) / 3));
    const [vat, setVat] = useState(null);
    const currentQ = Math.ceil((now.getMonth() + 1) / 3);
    const inAangifteMaand = [1, 4, 7, 10].includes(now.getMonth() + 1) && quarter === currentQ;

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get(`/admin/books/vat?year=${year}&quarter=${quarter}`);
            setVat(data);
        } catch {}
    }, [year, quarter]);

    useEffect(() => {
        load();
    }, [load]);

    if (!vat) return null;
    const r = vat.rubrieken;

    return (
        <div className={`${CARD} mt-6`} data-testid="vat-section">
            {inAangifteMaand && (
                <p data-testid="vat-deadline-banner" className="mb-4 border border-gold/40 bg-gold/10 p-3 text-xs leading-relaxed text-gold">
                    <Clock3 className="mr-2 inline h-3.5 w-3.5" />
                    Aangiftemaand — je BTW-aangifte én betaling voor {VAT_LABELS[quarter]} moeten uiterlijk <strong>{VAT_DEADLINES[quarter]}</strong> bij de Belastingdienst binnen zijn.
                </p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <p className={LABEL}>BTW per kwartaal — aangifte-concept</p>
                <div className="flex items-center gap-3">
                    <select data-testid="vat-quarter" value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} className="border border-white/15 bg-coal px-3 py-1.5 text-xs text-bone">
                        {[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}
                    </select>
                    <input data-testid="vat-year" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24 border border-white/15 bg-coal px-3 py-1.5 text-xs text-bone" />
                    <button data-testid="vat-download-bd" onClick={() => window.open(`${platformClient.defaults.baseURL}/admin/books/belastingdienst.pdf?year=${year}&quarter=${quarter}`, "_blank")} className="flex items-center gap-1 border border-gold/40 px-3 py-1.5 text-xs uppercase text-gold hover:bg-gold/10">
                        <Download className="h-3 w-3" /> Belastingdienst
                    </button>
                </div>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-4">
                <div className="border border-white/10 p-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-ash">Af te dragen btw (omzet)</p>
                    <p data-testid="vat-omzet-btw" className="mt-2 font-display text-2xl text-bone">{euro(r["1a_btw_hoog"] + r["1b_btw_laag"])}</p>
                </div>
                <div className="border border-white/10 p-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-ash">Voorbelasting (uitgaven)</p>
                    <p data-testid="vat-voorbelasting" className="mt-2 font-display text-2xl text-bone">{euro(r["5b_voorbelasting"])}</p>
                </div>
                <div className="border border-white/10 p-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-ash">Te betalen / terug</p>
                    <p data-testid="vat-saldo" className={`mt-2 font-display text-2xl ${r.te_betalen_of_terug >= 0 ? "text-red-400" : "text-green-400"}`}>{euro(Math.abs(r.te_betalen_of_terug))}</p>
                    <p className="mt-1 text-[10px] text-ash">{r.te_betalen_of_terug >= 0 ? "te betalen" : "terug te vragen"}</p>
                </div>
                <div className="border border-gold/30 bg-gold/5 p-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gold">Resultaat (omzet − kosten)</p>
                    <p data-testid="vat-resultaat" className="mt-2 font-display text-2xl text-bone">{euro(r.omzet_hoog + r.omzet_laag - r.kosten_netto_excl_btw)}</p>
                    <p className="mt-1 text-[10px] text-ash">excl. btw</p>
                </div>
            </div>
            <div className="mt-5 overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="border-b border-white/10 text-left text-[10px] uppercase tracking-[0.2em] text-ash">
                            <th className="pb-2 pr-4">Aangifte-rubriek</th><th className="pb-2 pr-4">Omzet</th><th className="pb-2">BTW</th>
                        </tr>
                    </thead>
                    <tbody className="text-bone">
                        <tr data-testid="vat-row-1a" className="border-b border-white/5">
                            <td className="py-2 pr-4">1a — Leveringen/diensten belast met hoog tarief</td>
                            <td className="py-2 pr-4">{euro(r["1a_omzet_belast_hoog"])}</td>
                            <td className="py-2">{euro(r["1a_btw_hoog"])}</td>
                        </tr>
                        <tr data-testid="vat-row-1b" className="border-b border-white/5">
                            <td className="py-2 pr-4">1b — Leveringen/diensten belast met laag tarief</td>
                            <td className="py-2 pr-4">{euro(r["1b_omzet_belast_laag"])}</td>
                            <td className="py-2">{euro(r["1b_btw_laag"])}</td>
                        </tr>
                        <tr data-testid="vat-row-5b">
                            <td className="py-2 pr-4">5b — Voorbelasting (btw op je uitgaven)</td>
                            <td className="py-2 pr-4">—</td>
                            <td className="py-2">{euro(r["5b_voorbelasting"])}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {vat.kor_hint ? (
                <p data-testid="vat-kor-hint" className="mt-4 border border-gold/20 bg-gold/5 p-3 text-xs leading-relaxed text-ash">
                    <Lightbulb className="mr-2 inline h-3.5 w-3.5 text-gold" />
                    Je omzet dit jaar ({euro(vat.omzet_jaar_totaal)}) zit onder de ca. €20.000 — de kleineondernemersregeling (KOR) kan interessant zijn. Zie het adviesblok hieronder.
                </p>
            ) : null}
            <p className="mt-4 text-[10px] leading-relaxed text-ash/70">{vat.disclaimer}</p>
        </div>
    );
};

export const BooksAdviceSection = () => {
    const [advice, setAdvice] = useState(null);
    useEffect(() => {
        platformClient.get("/admin/books/advice").then(({ data }) => setAdvice(data)).catch(() => {});
    }, []);

    if (!advice) return null;

    return (
        <div className={`${CARD} mt-6`} data-testid="advice-section">
            <p className={LABEL}><Lightbulb className="mr-2 inline h-3.5 w-3.5 text-gold" />Aftrekbaarheid & voordelen bij jouw SBI-codes</p>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
                {advice.sbi_advice.map((s) => (
                    <div key={s.sbi} data-testid={`sbi-${s.sbi}`} className="border border-white/10 p-5">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gold">SBI {s.sbi}</p>
                        <p className="mt-1 text-sm text-bone">{s.titel}</p>
                        <ul className="mt-3 space-y-2">
                            {s.tips.map((tip, i) => (
                                <li key={i} className="text-xs leading-relaxed text-ash">◆ {tip}</li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            <p className={`${LABEL} mt-8`}>Legale voordelen die vaak gemist worden</p>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                {advice.legal_benefits.map((b) => (
                    <div key={b.titel} data-testid={`benefit-${b.titel.slice(0, 12).replace(/\W/g, "-")}`} className="border border-white/10 p-5">
                        <p className="text-sm text-bone">{b.titel}</p>
                        <p className="mt-2 text-xs leading-relaxed text-ash">{b.body}</p>
                    </div>
                ))}
            </div>
            <p className="mt-5 text-[10px] leading-relaxed text-ash/70">{advice.disclaimer}</p>
        </div>
    );
};

const EMPTY_TIME = { date: new Date().toISOString().slice(0, 10), hours: "", description: "", project: "" };

export const BooksTimeSection = () => {
    const year = new Date().getFullYear();
    const [entries, setEntries] = useState([]);
    const [summary, setSummary] = useState(null);
    const [form, setForm] = useState(EMPTY_TIME);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        try {
            const [list, sum] = await Promise.all([
                platformClient.get(`/admin/books/time?year=${year}`),
                platformClient.get(`/admin/books/time/summary?year=${year}`),
            ]);
            setEntries(list.data);
            setSummary(sum.data);
        } catch {}
    }, [year]);

    useEffect(() => {
        load();
    }, [load]);

    const add = async (e) => {
        e.preventDefault();
        setError("");
        try {
            await platformClient.post("/admin/books/time", { ...form, hours: Number(form.hours) || 0 });
            setForm(EMPTY_TIME);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        await platformClient.delete(`/admin/books/time/${id}`);
        load();
    };

    return (
        <div className={`${CARD} mt-6`} data-testid="time-section">
            <p className={LABEL}><Clock3 className="mr-2 inline h-3.5 w-3.5 text-gold" />Urenlogboek {year} — urencriterium zelfstandigenaftrek</p>
            {summary ? (
                <div className="mt-5">
                    <div className="flex flex-wrap items-end justify-between gap-2">
                        <p className="text-sm text-ash">
                            <span data-testid="time-total" className="font-display text-3xl text-bone">{summary.totaal_uren}</span>
                            <span className="ml-2">van {summary.criterium} uur · nog {summary.resterend} uur</span>
                        </p>
                        <p data-testid="time-tempo" className={`text-[11px] uppercase tracking-[0.15em] ${summary.tempo_hint === "op schema" ? "text-gold" : "text-ash"}`}>{summary.tempo_hint}</p>
                    </div>
                    <div className="mt-3 h-1.5 w-full bg-white/10">
                        <div data-testid="time-progress" className="h-full bg-gold transition-all" style={{ width: `${summary.percentage}%` }} />
                    </div>
                    <p className="mt-3 border border-gold/20 bg-gold/5 p-3 text-xs leading-relaxed text-ash">
                        <Lightbulb className="mr-2 inline h-3.5 w-3.5 text-gold" />{summary.uitleg}
                    </p>
                </div>
            ) : null}

            <form onSubmit={add} className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-12">
                <label className="block md:col-span-2">
                    <span className={LABEL}>Datum</span>
                    <input data-testid="time-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className={inputCls} />
                </label>
                <label className="block md:col-span-2">
                    <span className={LABEL}>Uren</span>
                    <input data-testid="time-hours" type="number" step="0.25" min="0.25" max="24" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} required placeholder="bijv. 2.5" className={inputCls} />
                </label>
                <label className="col-span-2 block md:col-span-4">
                    <span className={LABEL}>Wat heb je gedaan?</span>
                    <input data-testid="time-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required placeholder="bijv. acquisitiegesprek, administratie…" className={inputCls} />
                </label>
                <label className="block md:col-span-2">
                    <span className={LABEL}>Project/klant</span>
                    <input data-testid="time-project" value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} placeholder="optioneel" className={inputCls} />
                </label>
                <div className="col-span-2 flex items-end md:col-span-2">
                    <button data-testid="time-submit" type="submit" className="border border-gold/50 px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black">
                        + Uren loggen
                    </button>
                </div>
                {error ? <p data-testid="time-error" className="col-span-2 text-sm text-red-400 md:col-span-12">{error}</p> : null}
            </form>

            {entries.length > 0 ? (
                <div className="mt-5 max-h-[280px] space-y-2 overflow-y-auto">
                    {entries.map((t) => (
                        <div key={t.id} data-testid={`time-row-${t.id}`} className="flex items-center justify-between gap-3 border-b border-white/5 pb-2 text-xs">
                            <div className="min-w-0">
                                <p className="truncate text-bone">{t.description} {t.project ? <span className="text-ash">· {t.project}</span> : null}</p>
                                <p className="text-[10px] uppercase tracking-wide text-ash">{t.date}</p>
                            </div>
                            <div className="flex shrink-0 items-center gap-3">
                                <span className="text-bone">{t.hours}u</span>
                                <button data-testid={`time-delete-${t.id}`} onClick={() => remove(t.id)} className="text-ash transition-colors hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export const BooksLegalSection = () => {
    const [blocks, setBlocks] = useState([]);
    useEffect(() => {
        platformClient.get("/admin/books/advice").then(({ data }) => setBlocks(data.legal_admin || [])).catch(() => {});
    }, []);

    if (blocks.length === 0) return null;

    return (
        <div className={`${CARD} mt-6`} data-testid="legal-admin-section">
            <p className={LABEL}><Scale className="mr-2 inline h-3.5 w-3.5 text-gold" />Wettelijk & administratie — wat je moet regelen</p>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                {blocks.map((b) => (
                    <div key={b.titel} data-testid={`legal-${b.titel.slice(0, 16).replace(/\W/g, "-")}`} className="border border-white/10 p-5">
                        <p className="text-sm text-bone">{b.titel}</p>
                        <ul className="mt-3 space-y-2">
                            {b.punten.map((p, i) => (
                                <li key={i} className="text-xs leading-relaxed text-ash">◆ {p}</li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            <p className="mt-5 text-[10px] leading-relaxed text-ash/70">Richtlijnen ter oriëntatie, geen juridisch of fiscaal advies — laat je contracten en aangifte controleren door een professional zodra je groeit.</p>
        </div>
    );
};
